import {atomWithoutDefault} from '@raison/toolbox';
import {useAtomValue} from 'jotai';
import React from 'react';

// shadowing fetch to make sure only fetchFn is used
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fetch = undefined;

export type HttpClientLike<TInit extends HttpClient.Init | void> = (
    url: HttpClient.Url,
    init: TInit
) => HttpClient.Output;

export type HttpClientEnhancer = <T extends HttpClient.Init | void>(
    next: HttpClientLike<T>,
    final: () => HttpClientLike<T>
) => HttpClientLike<T>;

export namespace HttpClientEnhancer {
    export const enhance = <T extends HttpClient.Init | void>(
        http: HttpClientLike<T>,
        enhancers: HttpClientEnhancer[]
    ): HttpClientLike<T> => {
        const getFinal = (): HttpClientLike<T> => finalHttp;
        const finalHttp = enhancers.reduceRight(
            (enhancedHttp, nextEnhancer) => nextEnhancer(enhancedHttp, getFinal),
            http
        );
        return finalHttp;
    };
}

export interface HttpClient extends HttpClientLike<HttpClient.Init | void> {}

export namespace HttpClient {
    export type Url = string;

    export interface Init extends RequestInit, BodyNeverUsed {}

    export type Inputs = Parameters<HttpClient>;

    export type QueryMethod = 'GET' | 'HEAD';
    export type MutationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    export type HttpMethod = QueryMethod | MutationMethod;

    export type BodySubset = Extract<BodyInit, FormData | Blob>;
    export type Body = object | BodySubset;

    export type SyncOutput = Response & {request: Request};
    export type Output = Promise<SyncOutput>;

    export const create = createHttpClient;
    export const base = create(window.fetch);
}

interface BodyNeverUsed {
    readonly bodyUsed?: {readonly NEVER: unique symbol};
}

const {assign} = Object;

export function createOutput(response: Response, request: Request) {
    return assign(response, {request});
}

function createHttpClient(fetch: (request: Request) => Promise<Response>): HttpClient {
    return async function http(...[url, init = {}]: HttpClient.Inputs) {
        const request = new Request(url, init);
        try {
            const response = await fetch(request);
            return createOutput(response, request);
        } catch (err) {
            if (err instanceof Error) {
                err.cause = new Error(`Failed attempt to request: ${request.method} ${request.url}`);
            }
            throw err;
        }
    };
}

export const HttpClient$ = atomWithoutDefault<HttpClient>('HttpClient$');

export function useHttpClient() {
    return useAtomValue(HttpClient$);
}

export const PublicHttpClient$ = atomWithoutDefault<HttpClient>('HttpClientPublic$');

export function usePublicHttpClient() {
    return useAtomValue(PublicHttpClient$);
}

type WithHttpClient = {
    http: HttpClient;
};

export function withHttpClient<P extends WithHttpClient>(Component: React.ComponentType<P>) {
    return function WithHttpClient(props: Omit<P, 'http'>) {
        const http = useHttpClient();
        return React.createElement(Component, {...props, http} as P);
    };
}

export function withPublicHttpClient<P extends WithHttpClient>(Component: React.ComponentType<P>) {
    return function WithHttpClient(props: Omit<P, 'http'>) {
        const http = usePublicHttpClient();
        return React.createElement(Component, {...props, http} as P);
    };
}

const mutationInitFactory =
    (method: HttpClient.HttpMethod) =>
    (bodyInit?: HttpClient.Body, extraInit: Omit<HttpClient.Init, 'body'> = {}): HttpClient.Init => {
        const headers = new Headers(extraInit.headers);

        let body: HttpClient.BodySubset | string | undefined;
        if (bodyInit instanceof Blob || bodyInit instanceof FormData || typeof bodyInit === 'string') {
            body = bodyInit;
        } else if (bodyInit == null) {
            body = undefined;
        } else {
            body = JSON.stringify(bodyInit);
            headers.set('content-type', 'application/json');
        }

        return {...extraInit, headers, method, body};
    };

export const POST = mutationInitFactory('POST');
export const PUT = mutationInitFactory('PUT');
export const DELETE = mutationInitFactory('DELETE');
export const PATCH = mutationInitFactory('PATCH');
